import React, { useMemo, PropsWithChildren } from "react"
import { useMediaQuery } from "react-responsive"
import { COLOR_SCHEMES, GENERIC_COLORS, GENERIC_COLORS_MAGAZIN } from "./ColorSchemes"
import { THEMED_IMAGES } from "./ThemedImages"
import _ from "lodash"
import { EmbedHost } from "./QuizEmbedInfoContext"
import { ILayoutContext, LayoutContext } from "./LayoutContext"

export type { ILayoutContext } from "./LayoutContext"
export { LayoutContext, useLayout } from "./LayoutContext"

const useGenericSchemeCssVars = (mode: ILayoutContext["mode"]) => {
  return useMemo<React.CSSProperties | undefined>(() => {
    const isDarkMode = mode === "dark"
    const combined = { ...GENERIC_COLORS, ...THEMED_IMAGES }
    return _.chain(combined).toPairs().map((pair) => [`--quiz__${pair[0]}`, pair[1][isDarkMode ? "dark" : "light"]]).fromPairs().value() as React.CSSProperties
  }, [mode])
}

export const DefineColorSchemeCSSVars: React.FC<PropsWithChildren<{embedHost: EmbedHost, palletteId: string, overrideColorTheme?: Quiz.Api.QuizColorTheme, darkMode?: boolean}>> = ({palletteId, children, darkMode, overrideColorTheme, embedHost}) => {
  const genericStyle = useGenericSchemeCssVars(darkMode ? "dark" : "light")

  const colorThemeStyle = useMemo<React.CSSProperties | undefined>(() => {
    const theme = overrideColorTheme || COLOR_SCHEMES[palletteId] || COLOR_SCHEMES[Object.keys(COLOR_SCHEMES)[0]]!
    const combined = { ...(embedHost === "magazin" ? GENERIC_COLORS_MAGAZIN : GENERIC_COLORS), ...theme }
    return {
      ...genericStyle,
      ..._.chain(combined).toPairs().map((pair) => [`--quiz__${pair[0]}`, pair[1][darkMode ? "dark" : "light"]]).fromPairs().value() as React.CSSProperties
    }
  }, [overrideColorTheme, darkMode, palletteId, genericStyle, embedHost])

  return <div style={colorThemeStyle}>
    {children}
  </div>
}

export const DefineGenericSchemeCSSVars: React.FC<PropsWithChildren<{overrideMode?: ILayoutContext["mode"]}>> = ({ children, overrideMode }) => {
  const nativeIsDarkMode = useMediaQuery({ query: "(prefers-color-scheme: dark)" })

  const colorThemeStyle = useGenericSchemeCssVars(overrideMode || (nativeIsDarkMode ? "dark" : "light"))

  return <div style={colorThemeStyle}>
    {children}
  </div>
}

export const MIN_WIDTH_FOR_DESKTOP = 600

export const WithLayout: React.FC<React.PropsWithChildren<{embedHost: EmbedHost, palletteId: string, overrideDesktopOrMobile?: "desktop" | "mobile", overrideColorTheme?: Quiz.Api.QuizColorTheme, forceMode?: ILayoutContext["mode"]}>> = ({palletteId, overrideColorTheme, children, forceMode, embedHost, overrideDesktopOrMobile}) => {
  const isLarge = useMediaQuery({ minWidth: MIN_WIDTH_FOR_DESKTOP }) // Keep in sync with CSS
  const nativeDarkMode = useMediaQuery({ query: "(prefers-color-scheme: dark)" })
  const isDarkMode = forceMode
    ? forceMode === "dark"
    : nativeDarkMode

  const val = useMemo<ILayoutContext>(() => ({
    type: overrideDesktopOrMobile ?? (isLarge ? "desktop" : "mobile"),
    palletteId,
    mode: isDarkMode ? "dark" : "light"
  }), [isLarge, isDarkMode, palletteId, overrideDesktopOrMobile])

  return (
    <LayoutContext.Provider value={val}>
      <DefineColorSchemeCSSVars embedHost={embedHost} palletteId={palletteId} darkMode={isDarkMode} overrideColorTheme={overrideColorTheme}>
        {children}
      </DefineColorSchemeCSSVars>
    </LayoutContext.Provider>
  )
}
